import * as BABYLON from 'babylonjs';
import * as Interfaces from '../../Interfaces/Interfaces';
import * as Enumerations from '../../Enumerations/Enumerations';
import { BlackAndWhiteScanPositionColor, BlackAndWhiteScanPositionGlowColor, isBlackAndWhiteScanPosition, isScanPositionView } from '../../helpers/viewMedia';
class ScanPositionManager implements Interfaces.Interfaces.IScanPositionManager {
  private _scene: BABYLON.Scene;
  private _eventHandler: Interfaces.Interfaces.IEventManager;

  private _colorScanVisible = false;
  private _blackAndWhiteScanVisible = false;
  private _imageVisible = false;
  private _spheres = new Map<string, BABYLON.Mesh>();
  private _viewsById = new Map<string, IView360Detail>();

  // materials
  private _baseScanMat: BABYLON.StandardMaterial | null = null;
  private _baseBlackAndWhiteScanMat: BABYLON.StandardMaterial | null = null;
  private _baseImageMat: BABYLON.StandardMaterial | null = null;
  private _hoverScanMat: BABYLON.StandardMaterial | null = null;
  private _hoverBlackAndWhiteScanMat: BABYLON.StandardMaterial | null = null;
  private _hoverImageMat: BABYLON.StandardMaterial | null = null;
  private _ambientLight: BABYLON.HemisphericLight | null = null;

  // hover handling
  private _hoveredId: string | null = null;
  private _pointerObs: BABYLON.Observer<BABYLON.PointerInfo> | null = null;

  // suppress re-emit when highlight is driven externally
  private _suppressHoverEvent = false;

  // colors
  private _scanSphereColor = '#00FFFB';
  private _imageSphereColor = '#F59300';
  private _glowScanColor = '#071F1C';
  private _glowBlackAndWhiteScanColor = BlackAndWhiteScanPositionGlowColor;
  private _glowImageColor = '#2A1600';

  constructor(eventHandler: Interfaces.Interfaces.IEventManager, scene: BABYLON.Scene) {
    this._scene = scene;
    this._eventHandler = eventHandler;
    this._ensureMaterials();
    this._attachPointerObserver();
  }

  public setScanSphereColor(sphereHex: string) {
    if (sphereHex) this._scanSphereColor = sphereHex;
    this._disposeMaterials();
    this._ensureMaterials();
    this._spheres.forEach((m) => m.material = this._getBaseMaterial(m));
    if (this._hoveredId && this._spheres.has(this._hoveredId)) {
      const mm = this._spheres.get(this._hoveredId);
      if (mm) mm.material = this._getHoverMaterial(mm);
    }
  }

  public setImageSphereColor(sphereHex: string) {
    if (sphereHex) this._imageSphereColor = sphereHex;
    this._disposeMaterials();
    this._ensureMaterials();
    this._spheres.forEach((m) => m.material = this._getBaseMaterial(m));
    if (this._hoveredId && this._spheres.has(this._hoveredId)) {
      const mm = this._spheres.get(this._hoveredId);
      if (mm) mm.material = this._getHoverMaterial(mm);
    }
  }

  public getScanSphereColor(): string {
    return this._scanSphereColor;
  }

  public getImageSphereColor(): string {
    return this._imageSphereColor;
  }

  dispose() {
    if (this._pointerObs) {
      this._scene.onPointerObservable.remove(this._pointerObs);
      this._pointerObs = null;
    }
    this._spheres.forEach((m) => m.dispose());
    this._spheres.clear();
    this._viewsById.clear();
    this._disposeMaterials();
    this._removeAmbientLight();
  }

  setScanPositionsVisible(visible: boolean) {
    this.setColorScanPositionsVisible(visible);
    this.setBlackAndWhiteScanPositionsVisible(visible);
  }

  setColorScanPositionsVisible(visible: boolean) {
    this._colorScanVisible = visible;
    this._updateScanPositionVisibility();
  }

  setBlackAndWhiteScanPositionsVisible(visible: boolean) {
    this._blackAndWhiteScanVisible = visible;
    this._updateScanPositionVisibility();
  }

  setImagePositionsVisible(visible: boolean) {
    this._imageVisible = visible;
    if (!visible) {
      this._clearHoverIfCurrentMatchesType(false);
    }
    this._spheres.forEach((m) => {
      const isScan = !!m.metadata?.__scan;
      if (!isScan) {
        m.setEnabled(visible);
      }
    });
  }

  private _clearHoverIfCurrentMatchesType(isScanType: boolean) {
    if (!this._hoveredId) return;

    const hoveredMesh = this._spheres.get(this._hoveredId);
    if (!hoveredMesh) return;

    const hoveredIsScan = !!hoveredMesh.metadata?.__scan;
    if (hoveredIsScan === isScanType) {
      this._applyHover(null);
    }
  }

  /**
   * Programmatically highlight a scan position by ID.
   * @param id View ID or null to clear
   * @param suppressEvent If true, do not fire SCANPOSITIONHIGHLIGHTED (prevents event loops)
   */
  public setHighlighted(id: string | null, suppressEvent: boolean = true) {
    const prev = this._suppressHoverEvent;
    this._suppressHoverEvent = !!suppressEvent;
    this._applyHover(id);
    this._suppressHoverEvent = prev;
  }

  /** Alias if callers prefer “hovered” terminology */
  public setHovered(id: string | null, suppressEvent: boolean = true) {
    this.setHighlighted(id, suppressEvent);
  }

  setPositions(items: IView360Detail[]) {
    const keep = new Set(items.map(i => i.ID));

    // remove stale
    Array.from(this._spheres.keys()).forEach((id) => {
      if (!keep.has(id)) {
        const mesh = this._spheres.get(id);
        if (mesh) mesh.dispose();
        this._spheres.delete(id);
        this._viewsById.delete(id);
      }
    });

    // upsert
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const pos = (it as any)?.Position;
      if (!pos) continue;

      this._viewsById.set(it.ID, it);

      let mesh = this._spheres.get(it.ID);

      const validRotation = isScanPositionView(it as any);
      const isBlackAndWhite = validRotation && isBlackAndWhiteScanPosition(it);

      if (!mesh) {
        mesh = BABYLON.MeshBuilder.CreateSphere(`scan_${it.ID}`, { diameter: 0.4 }, this._scene);
        mesh.isPickable = true;
        mesh.metadata = { __scan: validRotation, __blackAndWhite: isBlackAndWhite, __scanId: it.ID };
        mesh.material = this._getBaseMaterial(mesh);
        (mesh as any).dataSource = Enumerations.PickObjectType.ScanPos;
        this._spheres.set(it.ID, mesh);
      }

      mesh.metadata.__scan = validRotation;
      mesh.metadata.__blackAndWhite = isBlackAndWhite;
      mesh.material = this._getBaseMaterial(mesh);

      mesh.position.set(pos.X, pos.Y, pos.Z);
      mesh.setEnabled(validRotation
        ? (isBlackAndWhite ? this._blackAndWhiteScanVisible : this._colorScanVisible)
        : this._imageVisible);
    }

    if (this._spheres.size > 0) {
      this._setAmbientLight(Array.from(this._spheres.values()));
    }
    else {
      this._removeAmbientLight();
    }

    // if current hover was deleted, clear it
    if (this._hoveredId && !this._spheres.has(this._hoveredId)) {
      this._applyHover(null);
    }
  }

  // ---------- internals ----------

  private _setAmbientLight(meshes: BABYLON.Mesh[]) {
    if (!this._ambientLight) {
      this._ambientLight = new BABYLON.HemisphericLight("ambientLight", new BABYLON.Vector3(0, 1, 0), this._scene);
      this._ambientLight.intensity = 0.7;
    }
    this._ambientLight.includedOnlyMeshes = meshes; 
  }
  private _removeAmbientLight() {
    this._ambientLight?.dispose();
    this._ambientLight = null;
  }

  private _ensureMaterials() {
    if (!this._baseScanMat) {
      const m = new BABYLON.StandardMaterial('scanMaterial', this._scene);
      m.alpha = 0.3;
      m.diffuseColor = BABYLON.Color3.FromHexString(this._scanSphereColor);
      this._baseScanMat = m;
    }

    if (!this._baseImageMat) {
      const m = new BABYLON.StandardMaterial('imageMaterial', this._scene);
      m.alpha = 0.3;
      m.diffuseColor = BABYLON.Color3.FromHexString(this._imageSphereColor);
      this._baseImageMat = m;
    }

    if (!this._baseBlackAndWhiteScanMat) {
      const m = new BABYLON.StandardMaterial('blackAndWhiteScanMaterial', this._scene);
      m.alpha = 0.3;
      m.diffuseColor = BABYLON.Color3.FromHexString(BlackAndWhiteScanPositionColor);
      this._baseBlackAndWhiteScanMat = m;
    }

    if (!this._hoverScanMat) {
      const g = this._baseScanMat.clone('scanMaterial_glow') as BABYLON.StandardMaterial;
      g.emissiveColor = BABYLON.Color3.FromHexString(this._glowScanColor);
      g.alpha = 0.75;
      this._hoverScanMat = g;
    }

    if (!this._hoverImageMat) {
      const g = this._baseImageMat.clone('imageMaterial_glow') as BABYLON.StandardMaterial;
      g.emissiveColor = BABYLON.Color3.FromHexString(this._glowImageColor);
      g.alpha = 0.75;
      this._hoverImageMat = g;
    }
    if (!this._hoverBlackAndWhiteScanMat) {
      const g = this._baseBlackAndWhiteScanMat.clone('blackAndWhiteScanMaterial_glow') as BABYLON.StandardMaterial;
      g.emissiveColor = BABYLON.Color3.FromHexString(this._glowBlackAndWhiteScanColor);
      g.alpha = 0.75;
      this._hoverBlackAndWhiteScanMat = g;
    }
  }

  private _disposeMaterials() {
    this._hoverScanMat?.dispose(); this._hoverScanMat = null;
    this._hoverBlackAndWhiteScanMat?.dispose(); this._hoverBlackAndWhiteScanMat = null;
    this._hoverImageMat?.dispose(); this._hoverImageMat = null;
    this._baseScanMat?.dispose(); this._baseScanMat = null;
    this._baseBlackAndWhiteScanMat?.dispose(); this._baseBlackAndWhiteScanMat = null;
    this._baseImageMat?.dispose(); this._baseImageMat = null;
  }

  private _attachPointerObserver() {
    this._pointerObs = this._scene.onPointerObservable.add((pi) => {
      if (pi.type === BABYLON.PointerEventTypes.POINTERMOVE) {
        if (!this._colorScanVisible && !this._blackAndWhiteScanVisible && !this._imageVisible) { this._applyHover(null); return; }

        const pick = this._scene.pick(
          this._scene.pointerX,
          this._scene.pointerY,
          (m) =>
            !!m &&
            m.isPickable &&
            m.isEnabled() &&
            m.isVisible &&
            m.metadata?.__scan != null
        );

        const id =
          pick && pick.hit && pick.pickedMesh && pick.pickedMesh.metadata
            ? (pick.pickedMesh.metadata.__scanId as string)
            : null;

        // pointer-driven highlight should emit events
        this._applyHover(id);
        return;
      }

      if (pi.type === BABYLON.PointerEventTypes.POINTERUP) {
        const pointerEvent = pi.event as PointerEvent | undefined;
        if (pointerEvent?.button === 2) {
          pointerEvent.preventDefault();
          pointerEvent.stopPropagation();
          this._emitRightClick(pointerEvent);
        }
      }
    });
  }

  private _emitRightClick(pointerEvent?: PointerEvent) {
    if (!this._hoveredId) return;
    const view = this._viewsById.get(this._hoveredId) || null;
    const position = pointerEvent
      ? { clientX: pointerEvent.clientX, clientY: pointerEvent.clientY }
      : null;
    this._eventHandler.fireEvent(Enumerations.EventType.SCANPOSITIONRIGHTCLICKED, { view, position });
  }

  private _applyHover(id: string | null) {
    if (this._hoveredId === id) return;

    if (this._hoveredId && this._spheres.has(this._hoveredId)) {
      const prev = this._spheres.get(this._hoveredId)!;
      prev.material = this._getBaseMaterial(prev);
    }

    this._hoveredId = id;

    if (this._hoveredId && this._spheres.has(this._hoveredId)) {
      const cur = this._spheres.get(this._hoveredId)!;
      cur.material = this._getHoverMaterial(cur);

      const view = this._viewsById.get(this._hoveredId) || null;
      if (!this._suppressHoverEvent) {
        this._eventHandler.fireEvent(Enumerations.EventType.SCANPOSITIONHIGHLIGHTED, { view });
      }
    } else {
      if (!this._suppressHoverEvent) {
        this._eventHandler.fireEvent(Enumerations.EventType.SCANPOSITIONHIGHLIGHTED, { view: null });
      }
    }

    this._scene.render();
  }

  private _updateScanPositionVisibility() {
    this._spheres.forEach((mesh) => {
      if (mesh.metadata?.__scan) {
        mesh.setEnabled(mesh.metadata.__blackAndWhite
          ? this._blackAndWhiteScanVisible
          : this._colorScanVisible);
      }
    });

    if (this._hoveredId && !this._spheres.get(this._hoveredId)?.isEnabled()) {
      this._applyHover(null);
    }
  }

  private _getBaseMaterial(mesh: BABYLON.Mesh): BABYLON.Material | null {
    if (!mesh.metadata?.__scan) return this._baseImageMat;
    return mesh.metadata.__blackAndWhite ? this._baseBlackAndWhiteScanMat : this._baseScanMat;
  }

  private _getHoverMaterial(mesh: BABYLON.Mesh): BABYLON.Material | null {
    if (!mesh.metadata?.__scan) return this._hoverImageMat;
    return mesh.metadata.__blackAndWhite ? this._hoverBlackAndWhiteScanMat : this._hoverScanMat;
  }
}

export default ScanPositionManager;
